/**
 * @module features/devices/api/telemetry-api
 * @description Snapshot fetcher + live hook for the device telemetry dashboard.
 * Live updates use a fetch-based Server-Sent-Events reader (EventSource cannot send
 * the Authorization header). If the stream drops or is blocked by a proxy, the hook
 * falls back to 5s TanStack Query polling and retries the stream periodically.
 * The log list is a rolling window of at most 30 entries, newest first.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "@/config/env";
import { apiClient, ApiSuccessEnvelope, AppError, getAccessToken } from "@/lib/api-client";
import {
  DeviceTelemetryDashboardData,
  DeviceTelemetryDashboardDataSchema,
  DisplayModuleTelemetryLog,
  DisplayModuleTelemetryLogSchema,
  RETENTION_LIMIT,
} from "../types/telemetry";
import { parseOrThrow } from "./credentials-api";

const BASE = "/super-admin/devices";
export const POLL_INTERVAL_MS = 5000;
const STREAM_RETRY_MS = 15000;

export const telemetryKeys = {
  dashboard: (id: string) => ["devices", "telemetry", id] as const,
};

export type StreamState = "connecting" | "live" | "polling" | "paused";

export async function getDeviceTelemetryDashboard(
  deviceId: string
): Promise<DeviceTelemetryDashboardData> {
  try {
    const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(`${BASE}/${deviceId}/telemetry/`);
    return parseOrThrow(DeviceTelemetryDashboardDataSchema, res.data.data, res.data.request_id);
  } catch (err) {
    throw AppError.fromUnknown(err, "Failed to load device telemetry.");
  }
}

/** Prepend `incoming`, de-duplicate by id and keep only the newest 30 (FIFO). */
export function mergeLogs(
  prev: DisplayModuleTelemetryLog[],
  incoming: DisplayModuleTelemetryLog
): DisplayModuleTelemetryLog[] {
  const rest = prev.filter((l) => l.id !== incoming.id);
  return [incoming, ...rest]
    .sort((a, b) => b.received_timestamp.localeCompare(a.received_timestamp))
    .slice(0, RETENTION_LIMIT);
}

/** Split a buffer into complete SSE frames; returns parsed telemetry payloads + remainder. */
export function parseSseBuffer(buffer: string): {
  events: DisplayModuleTelemetryLog[];
  rest: string;
} {
  const frames = buffer.split("\n\n");
  const rest = frames.pop() ?? "";
  const events: DisplayModuleTelemetryLog[] = [];
  for (const frame of frames) {
    const lines = frame.split("\n");
    if (!lines.some((l) => l.trim() === "event: telemetry")) continue;
    const data = lines
      .filter((l) => l.startsWith("data:"))
      .map((l) => l.slice(5).trim())
      .join("\n");
    try {
      const parsed = DisplayModuleTelemetryLogSchema.safeParse(JSON.parse(data));
      if (parsed.success) events.push(parsed.data);
    } catch {
      /* malformed frame: skip, the next poll/snapshot will reconcile */
    }
  }
  return { events, rest };
}

interface Options {
  /** When false the stream is closed and polling stops ("Paused"). */
  live: boolean;
}

export function useDeviceTelemetryDashboard(deviceId: string | undefined, { live }: Options) {
  const [streamState, setStreamState] = React.useState<StreamState>("connecting");
  const [logs, setLogs] = React.useState<DisplayModuleTelemetryLog[]>([]);

  const query = useQuery({
    queryKey: telemetryKeys.dashboard(deviceId ?? ""),
    queryFn: () => getDeviceTelemetryDashboard(deviceId as string),
    enabled: Boolean(deviceId),
    refetchInterval: live && streamState !== "live" ? POLL_INTERVAL_MS : false,
  });

  // Snapshot is the source of truth; reconcile whenever it changes.
  React.useEffect(() => {
    if (query.data) setLogs(query.data.logs);
  }, [query.data]);

  React.useEffect(() => {
    if (!deviceId || !live || !query.isSuccess) {
      setStreamState(live ? "connecting" : "paused");
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = async () => {
      setStreamState("connecting");
      try {
        const token = getAccessToken();
        const res = await fetch(`${API_BASE_URL}${BASE}/${deviceId}/telemetry/stream/`, {
          headers: {
            Accept: "text/event-stream",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`stream_unavailable_${res.status}`);
        setStreamState("live");
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { events, rest } = parseSseBuffer(buffer);
          buffer = rest;
          if (events.length && !cancelled) {
            setLogs((prev) => events.reduce(mergeLogs, prev));
          }
        }
      } catch {
        /* network/proxy failure: fall through to polling */
      }
      if (cancelled) return;
      setStreamState("polling");
      retryTimer = setTimeout(connect, STREAM_RETRY_MS);
    };
    void connect();

    return () => {
      cancelled = true;
      controller.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [deviceId, live, query.isSuccess]);

  return { ...query, logs, streamState };
}
